import { FormattedMessage } from 'react-intl'

import type { BrandFeatures } from 'apiTypes/BrandFeatures'
import { BrandSteps } from 'apiTypes/BrandSteps'

import { getDataToReview } from 'lib/getDataToReview'

import { ButtonGroup, ButtonSolid } from 'components/Buttons'
import { IconDocumentCheck, IconDocumentEye, IconDocumentWrite } from 'components/Icons'
import { Card } from 'components/Layout'
import { OrderedItem, OrderedList } from 'components/OrderedList'

import type { Step, StepType } from '../../Step'
import type { StepHandlers } from '../../stepHandler'
import type { UserMetadata } from '../../UserMetadata'

type Props = TaggedType<Step, StepType.Introduction> & {
	userMetadata: UserMetadata
	brandFeatures: Array<BrandFeatures>

	onNext: StepHandlers['onIntroductionNext']
}

export const Introduction = ({ brandSteps, userMetadata, brandFeatures, onNext }: Props) => {
	const { advisorName, applicantType } = userMetadata
	const hasAssetsStep = brandSteps.includes(BrandSteps.Assets)
	const hasBudgetStep = brandSteps.includes(BrandSteps.BudgetEditForm)
	const dataToReview = getDataToReview(brandFeatures)

	return (
		<Card>
			<p>
				<FormattedMessage
					id='flow.introduction.description'
					defaultMessage='{applicantType, select,
						other { Your advisor, {advisorName}, needs an overview of your {dataToReview, select, expenses {fixed expenses} income {recurring income} both {fixed expenses and recurring income} other {budget}} to process your loan application. For this purpose, Nordea collaborates with Subaio. }
					}'
					values={{ advisorName, dataToReview, applicantType /* Necessary for other languages than English */ }}
				/>
			</p>
			<OrderedList>
				<OrderedItem
					icon={<IconDocumentEye />}
					title={
						<>
							<FormattedMessage id='flow.introduction.stepNotation1' defaultMessage='Step 1' />
							<span>: </span>
							<FormattedMessage id='flow.introduction.dataCollection.title' defaultMessage='Data collection' />
						</>
					}
				>
					<FormattedMessage
						id='flow.introduction.dataCollection.description'
						defaultMessage='{applicantType, select,
							other { With your consent, Subaio collects {dataToReview, select, expenses {payment data} income {income data} both {payment and income data} other {financial data}} from your bank accounts in Nordea and other banks for the past 12 months. This helps provide an overview of your current {dataToReview, select, expenses {fixed expenses} income {recurring income} both {fixed expenses and recurring income} other {budget}}. }
						}'
						values={{ dataToReview, applicantType /* Necessary for other languages than English */ }}
					/>
				</OrderedItem>
				{hasAssetsStep && (
					<OrderedItem
						icon={<IconDocumentWrite />}
						title={
							<>
								<FormattedMessage id='flow.introduction.stepNotation2' defaultMessage='Step 2' />
								<span>: </span>
								{}
								<FormattedMessage id='flow.introduction.assets.title' defaultMessage='Add assets' />
							</>
						}
					>
						<FormattedMessage
							id='flow.introduction.assets.description'
							defaultMessage='{applicantType, select, other { You can optionally add housing and transport assets that should be included in your budget before reviewing the numbers. }}'
							values={{ applicantType }}
						/>
					</OrderedItem>
				)}
				{hasBudgetStep && (
					<OrderedItem
						icon={<IconDocumentWrite />}
						title={
							<>
								{hasAssetsStep ? (
									<FormattedMessage id='flow.introduction.stepNotation3' defaultMessage='Step 3' />
								) : (
									<FormattedMessage id='flow.introduction.stepNotation2' defaultMessage='Step 2' />
								)}
								<span>: </span>
								<FormattedMessage id='flow.introduction.budget.title' defaultMessage='Budget overview' />
							</>
						}
					>
						<FormattedMessage
							id='flow.introduction.budget.description'
							defaultMessage='{applicantType, select, other { Once the data is collected, you will be presented with your budget overview, where your {dataToReview, select, expenses {fixed expenses are} income {recurring income is} both {fixed expenses and recurring income are} other {budget is}} grouped into categories. If you notice significant deviations, you can adjust the amounts. If you make changes, please describe them in the comment field. }}'
							values={{ applicantType, dataToReview }}
						/>
					</OrderedItem>
				)}
				<OrderedItem
					icon={<IconDocumentCheck />}
					title={
						<>
							{hasAssetsStep && hasBudgetStep ? (
								<FormattedMessage id='flow.introduction.stepNotation4' defaultMessage='Step 4' />
							) : hasAssetsStep || hasBudgetStep ? (
								<FormattedMessage id='flow.introduction.stepNotation3' defaultMessage='Step 3' />
							) : (
								<FormattedMessage id='flow.introduction.stepNotation2' defaultMessage='Step 2' />
							)}
							<span>: </span>
							<FormattedMessage id='flow.introduction.submitBudget.title' defaultMessage='Send budget' />
						</>
					}
				>
					{hasBudgetStep ? (
						<FormattedMessage
							id='flow.introduction.submitBudget.description'
							defaultMessage='{applicantType, select, other { When you are satisfied with your budget overview, it will be sent to {advisorName}, who will use it for your loan application. {advisorName} only sees the overall categories in your budget, not individual transactions or merchants. }}'
							values={{ advisorName, applicantType /* Necessary for other languages than English */ }}
						/>
					) : (
						<FormattedMessage
							id='flow.introduction.submitBudget.descriptionShort'
							defaultMessage='{applicantType, select, other { When you are satisfied with your budget overview, it will be sent to {advisorName}, who will use it for your loan application. }}'
							values={{ advisorName, applicantType /* Necessary for other languages than English */ }}
						/>
					)}
				</OrderedItem>
			</OrderedList>
			<ButtonGroup>
				<ButtonSolid
					label={<FormattedMessage id='flow.buttonNext' defaultMessage='Continue' />}
					onClick={() => onNext()}
				/>
			</ButtonGroup>
		</Card>
	)
}
